import { Temporal } from "temporal-polyfill";
import type { TimeRange } from "./time-range-selection.ts";
import type { ViewType } from "./types.ts";

/**
 * A range expressed in wall-clock terms on a specific date, suitable for URL
 * encoding. Times are "HH:mm" (24h) on `date` (YYYY-MM-DD) in the user's IANA
 * timezone. Carries its own date so ranges are self-contained and can span
 * different days in a single link (multi-day selections).
 *
 * **Midnight-end convention**: an `end` of `"00:00"` means "midnight of the
 * day after `date`" — i.e. the end of `date`. This is unambiguous because
 * `end` must be strictly after `start` on the same date, so `end = "00:00"`
 * can never mean the start of `date`. Use {@link linkRangeToInstants} (or
 * replicate its logic) when converting back to absolute times so the end
 * instant isn't silently 24 hours too early.
 */
export interface CalendarLinkRange {
  date: string; // "YYYY-MM-DD"
  start: string; // "HH:mm"
  end: string; // "HH:mm" — "00:00" means end-of-day (midnight of date+1)
}

/**
 * A calendar focus link's payload — which day to show, in which view, with which
 * ranges highlighted. Deliberately carries no event id: a specific event is its
 * own entity with its own path (`/calendar/event/:id`), and presenting it already
 * moves the calendar to its day.
 */
export interface CalendarLinkParts {
  /** Optional initial view date. When omitted, the consumer opens on the first range's date. */
  date?: string;
  /** Optional initial view type (day / week / month). */
  view?: ViewType;
  /** Optional preselected ranges to highlight + chip in the omnibox. */
  ranges?: CalendarLinkRange[];
}

function parseViewType(raw: string | null): ViewType | undefined {
  if (raw === "day" || raw === "multi-day" || raw === "week" || raw === "month") return raw;
  return undefined;
}

const PATH_PREFIX = "/calendar";

/**
 * The canonical page path for a single event, keyed by the source ent id
 * FullCalendar carries as `gcalId`.
 *
 * This is the one address an event answers to: the navigation boundaries map it
 * to a canvas card where a canvas is mounted ({@link canvasContentForPath}) and
 * let it load the real page everywhere else, so callers emit this rather than
 * choosing a surface themselves.
 */
export const eventDetailPath = (eventId: string): string => `${PATH_PREFIX}/event/${eventId}`;

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function formatDate(y: number, m: number, d: number): string {
  return `${String(y).padStart(4, "0")}-${pad2(m)}-${pad2(d)}`;
}

/**
 * Encode a range as "YYYY-MM-DDTHH:mm-HH:mm". Self-contained — carries its own
 * date so the consumer doesn't need to piggyback on the `date` URL param.
 */
export function encodeLinkRange(r: CalendarLinkRange): string {
  return `${r.date}T${r.start}-${r.end}`;
}

/**
 * Parse the compact `"YYYY-MM-DDTHH:mm-HH:mm"` range encoding back into fields.
 * Returns null for malformed input.
 */
export function parseLinkRange(raw: string): CalendarLinkRange | null {
  const tIdx = raw.indexOf("T");
  if (tIdx < 0) return null;
  const date = raw.slice(0, tIdx);
  const timePart = raw.slice(tIdx + 1);
  const dashIdx = timePart.indexOf("-");
  if (dashIdx < 0) return null;
  const start = timePart.slice(0, dashIdx);
  const end = timePart.slice(dashIdx + 1);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  if (!/^\d{2}:\d{2}$/.test(start) || !/^\d{2}:\d{2}$/.test(end)) return null;
  return { date, start, end };
}

/**
 * Build a calendar URL. The path (`/calendar`) is the real calendar page
 * route; the payload (view date, view type, ranges) rides in query params,
 * matching the right-sidebar's native `sb_*` query-param convention.
 *
 *   /calendar                                                          (empty — not useful, but valid)
 *   /calendar?date=2026-04-27                                          (date only)
 *   /calendar?date=2026-04-27&view=week                                (date + view)
 *   /calendar?r=2026-04-27T09:00-12:00                                 (single range; view = first range date)
 *   /calendar?r=2026-04-27T09:00-12:00&r=2026-04-27T14:00-16:00        (same day, multi-range)
 *   /calendar?r=2026-04-27T09:00-12:00&r=2026-04-28T14:00-16:00        (cross-day, multi-range)
 */
export function buildCalendarUrl(parts: CalendarLinkParts): string {
  const params = new URLSearchParams();
  if (parts.date) params.set("date", parts.date);
  if (parts.view) params.set("view", parts.view);
  for (const r of parts.ranges ?? []) {
    params.append("r", encodeLinkRange(r));
  }
  const q = params.toString();
  return q ? `${PATH_PREFIX}?${q}` : PATH_PREFIX;
}

/**
 * Parse a `URLSearchParams` into calendar parts. The path-less counterpart to
 * `parseCalendarUrl` — consumes the query string directly, which is how the
 * `/calendar` page reads its own URL state. Malformed `r=` entries and
 * unknown `view=` values are silently dropped.
 */
export function parseCalendarParams(params: URLSearchParams): CalendarLinkParts {
  const date = params.get("date") ?? undefined;
  const view = parseViewType(params.get("view"));
  const ranges = params
    .getAll("r")
    .map(parseLinkRange)
    .filter((r): r is CalendarLinkRange => r !== null);
  return {
    date,
    view,
    ranges: ranges.length > 0 ? ranges : undefined,
  };
}

/**
 * Convert a `TimeRange` (absolute instants) into a wall-clock link range in the
 * given IANA timezone. Ranges that span midnight are split by the caller via
 * `decomposeBoxSelection` before reaching this function; here we assume a single
 * wall-clock day.
 */
export function timeRangeToLinkRange(range: TimeRange, timezone: string): CalendarLinkRange {
  const startZdt = Temporal.Instant.from(range.startISO).toZonedDateTimeISO(timezone);
  const endZdt = Temporal.Instant.from(range.endISO).toZonedDateTimeISO(timezone);
  return {
    date: formatDate(startZdt.year, startZdt.month, startZdt.day),
    start: `${pad2(startZdt.hour)}:${pad2(startZdt.minute)}`,
    end: `${pad2(endZdt.hour)}:${pad2(endZdt.minute)}`,
  };
}

/**
 * Convert epoch-ms start/end (the shape stored on most artifacts) into a
 * wall-clock link range in the given IANA timezone. Sibling of
 * `timeRangeToLinkRange`, which takes ISO instants. Caller MUST ensure
 * `start` and `end` fall on the same wall-clock day in `timezone` —
 * the returned `CalendarLinkRange` carries one `date`, so multi-day spans
 * silently lose their end-day information. Use `eventToCalendarLinkParts`
 * (which guards this) instead of calling this directly.
 */
export function eventMsToLinkRange(
  startMs: number,
  endMs: number,
  timezone: string,
): CalendarLinkRange {
  const startZdt = Temporal.Instant.fromEpochMilliseconds(startMs).toZonedDateTimeISO(timezone);
  const endZdt = Temporal.Instant.fromEpochMilliseconds(endMs).toZonedDateTimeISO(timezone);
  return {
    date: formatDate(startZdt.year, startZdt.month, startZdt.day),
    start: `${pad2(startZdt.hour)}:${pad2(startZdt.minute)}`,
    end: `${pad2(endZdt.hour)}:${pad2(endZdt.minute)}`,
  };
}

/**
 * Compute the calendar focus link parts for an event's timestamps: its day, plus
 * the time range to highlight. Pure function, no React or hooks — lives here next
 * to the other date-link encoding helpers and is unit-testable separately from
 * `EventDateLink`.
 *
 * The range is skipped (`ranges` undefined) when:
 *   - the event is all-day, or
 *   - no `endMs` was supplied, or
 *   - start and end fall on different wall-clock days in `timezone` (a
 *     `CalendarLinkRange` carries a single `date`, so multi-day spans
 *     can't be encoded losslessly — we'd silently drop the end-day's
 *     date and it would decode as a same-day range).
 *
 * In all skipped cases the link still lands on the start day; the user can
 * navigate forward to see the rest of a multi-day event.
 */
export function eventToCalendarLinkParts(
  startMs: number,
  endMs: number | undefined,
  isAllDay: boolean | undefined,
  timezone: string,
): CalendarLinkParts {
  const startZdt = Temporal.Instant.fromEpochMilliseconds(startMs).toZonedDateTimeISO(timezone);
  const date = formatDate(startZdt.year, startZdt.month, startZdt.day);

  if (isAllDay || endMs === undefined) {
    return { date };
  }
  const endZdt = Temporal.Instant.fromEpochMilliseconds(endMs).toZonedDateTimeISO(timezone);
  const sameDay =
    endZdt.year === startZdt.year && endZdt.month === startZdt.month && endZdt.day === startZdt.day;
  if (!sameDay) {
    return { date };
  }
  return { date, ranges: [eventMsToLinkRange(startMs, endMs, timezone)] };
}

/**
 * Resolve a `CalendarLinkRange` into absolute start/end instants (ISO strings)
 * in the given IANA timezone, applying the midnight-end convention: an `end` of
 * `"00:00"` is interpreted as "midnight of the day after `date`" (i.e. the end
 * of `date`) rather than the start of `date`. Without this shift, a last-slot
 * selection (23:30–00:00 next day) would round-trip to an inverted range and
 * disappear from the grid.
 */
export function linkRangeToInstants(
  range: CalendarLinkRange,
  timezone: string,
): { startISO: string; endISO: string } {
  const toISO = (date: string, hhmm: string) =>
    Temporal.PlainDateTime.from(`${date}T${hhmm}:00`)
      .toZonedDateTime(timezone)
      .toInstant()
      .toString();
  const startISO = toISO(range.date, range.start);
  const endDate =
    range.end === "00:00"
      ? Temporal.PlainDate.from(range.date).add({ days: 1 }).toString()
      : range.date;
  const endISO = toISO(endDate, range.end);
  return { startISO, endISO };
}

/**
 * Parse a calendar URL emitted by `buildCalendarUrl`. Returns null if the URL
 * isn't a calendar URL (path doesn't match). Missing fields surface as
 * undefined/empty. Malformed `r=` entries are silently dropped.
 */
export function parseCalendarUrl(href: string): CalendarLinkParts | null {
  const qIdx = href.indexOf("?");
  const path = qIdx >= 0 ? href.slice(0, qIdx) : href;
  if (path !== PATH_PREFIX) return null;
  const search = qIdx >= 0 ? href.slice(qIdx + 1) : "";
  return parseCalendarParams(new URLSearchParams(search));
}
